import { supabase } from '../lib/supabase'
import { treatmentOf, gstComponent } from '../lib/gstTreatment'
import { Deal } from '../types'
import { isOnHandStatus, isSoldStatus } from '../lib/dealLifecycle'
import { statusLabel, type DealStatus } from '../lib/dealStatusService'

/**
 * Dashboard ("Today") service.
 *
 * Composes a single snapshot for the Today action-stream from live staging
 * tables (deals, payment_runs, invoices). Read-only. The presentation fields
 * (icons, colours, sparklines) are attached here so the view stays declarative
 * and matches the Direct Auto design system.
 */

const DAY_MS = 86_400_000


const yearOf = (d: Deal) => d.autograb_year ?? d.vehicle_year
const makeOf = (d: Deal) => d.autograb_make || d.vehicle_make || ''
const modelOf = (d: Deal) => d.autograb_model || d.vehicle_model || ''
const vehicleLabel = (d: Deal) => `${yearOf(d) ?? ''} ${makeOf(d)} ${modelOf(d)}`.trim()

const startOfMonth = () => {
  const n = new Date()
  return new Date(n.getFullYear(), n.getMonth(), 1).getTime()
}
const startOfWeek = () => Date.now() - 7 * DAY_MS
const daysSince = (iso?: string | null) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS) : 0

export type ActionSeverity = 'urgent' | 'go' | 'info' | 'done'

export interface ActionItem {
  key: string
  kicker: string
  title: string
  detail: string
  severity: ActionSeverity
  icon: string // tabler icon class e.g. 'ti-cash'
  iconBg: string // css var
  iconColor: string // css var
  view: string
  amount?: string
  amountColor?: string
}

export interface HeroStat {
  label: string
  value: string
  detail: string
  icon: string
  trend: 'up' | 'down' | ''
  view: string
}

export interface BrainInsight {
  kind: 'pace' | 'risk' | 'opportunity'
  label: string
  value: string
  body: string
  color: string
  icon: string
  spark: number[]
  view: string
}

export interface FlowStage {
  label: string
  count: number
}

export interface DashboardSnapshot {
  eyebrow: string
  headline: string
  sub: string
  hero: HeroStat[]
  needsYou: ActionItem[]
  comingUp: ActionItem[]
  handled: ActionItem[]
  insights: BrainInsight[]
  recommendation: { text: string; view: string } | null
  flow: FlowStage[]
}

const money = (n: number) => '$' + Math.round(n).toLocaleString('en-AU')
const kmoney = (n: number) =>
  Math.abs(n) >= 1000 ? '$' + (Math.round(n / 100) / 10).toLocaleString('en-AU') + 'k' : money(n)

function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

// Decorative sparkline (no randomness — deterministic from a seed value).
function ramp(seed: number, up: boolean): number[] {
  const base = 30 + (Math.abs(Math.round(seed)) % 25)
  const out: number[] = []
  for (let i = 0; i < 6; i++) {
    const step = up ? i : 5 - i
    out.push(base + step * 11)
  }
  return out
}

export async function loadDashboard(firstName?: string): Promise<DashboardSnapshot> {
  const now = new Date()
  const monthStart = startOfMonth()
  /*
   * Winnie: "Need to show the figure is actual MTD." MTD on its own does not
   * say which days it covers, so a reader cannot tell month-to-date from a
   * rolling 30 days or a whole-month projection. Naming the range removes the
   * guess.
   */
  const mtdRange = `1\u2013${now.getDate()} ${now.toLocaleString('en-AU', { month: 'short' })}`
  const weekStart = startOfWeek()

  const [dealsRes, runsRes, invoicesRes] = await Promise.all([
    supabase.from('deals').select('*').is('deleted_at', null),
    supabase.from('payment_runs').select('id, total_amount, status, winnie_review_complete, created_at'),
    supabase
      .from('invoices')
      .select('id, deal_id, invoice_number, buyer_name, total_amount, due_date, status'),
  ])
  if (dealsRes.error) throw dealsRes.error

  const deals = (dealsRes.data || []) as Deal[]
  const runs = (runsRes.data || []) as any[]

  /*
   * Invoices on archived deals are left out, the same as in the invoice ledger and
   * the reminder ladder. Otherwise the "to chase" figure counts money against
   * vehicles that no longer appear anywhere in the platform, and there is nothing
   * the person reading the tile can do about it.
   */
  const liveDealIds = new Set(deals.map((d) => d.id))
  const invoices = ((invoicesRes.data || []) as any[]).filter(
    (i) => !i.deal_id || liveDealIds.has(i.deal_id),
  )

  // ---------- core aggregates ----------

  const soldThisMonth = deals.filter(
    (d) =>
      /* This was a two-item literal written before sold_in_transit and sold_delivered
         existed, so every sale made through the Sale Details Workspace was missing
         from this figure. isSoldStatus is the canonical list. */
      isSoldStatus(d.deal_status) &&
      new Date(d.updated_at).getTime() >= monthStart
  )
  const netProfitMtd = soldThisMonth.reduce((s, d) => s + (d.gross_margin || 0), 0)

  /*
   * A run with no live vehicles attached is not something anyone can sign off, so
   * it does not go in the queue. That covers both an empty run left behind by a
   * test and a run whose vehicles have since been archived. The Payment Approval
   * screen still lists it, which is where such a run can actually be dealt with.
   */
  const runIdsWithLiveDeals = new Set(deals.map((d) => d.payment_run_id).filter(Boolean))
  const pendingRuns = runs.filter(
    (r) => ['pending_winnie', 'pending_tuo'].includes(r.status) && runIdsWithLiveDeals.has(r.id),
  )
  const runAmount = (r: any) => Number(r.total_amount) || 0
  const awaitingApproval = pendingRuns.reduce((s, r) => s + runAmount(r), 0)
  const winnieRuns = pendingRuns.filter((r) => r.status === 'pending_winnie')
  const tuoRuns = pendingRuns.filter((r) => r.status === 'pending_tuo')

  const openInvoices = invoices.filter((i) => ['sent', 'overdue', 'disputed'].includes(i.status))
  const invAmount = (i: any) => Number(i.total_amount) || 0
  const overdueInvoices = openInvoices.filter(
    (i) => i.due_date && new Date(i.due_date).getTime() < Date.now()
  )
  const arOverdue = overdueInvoices.reduce((s, i) => s + invAmount(i), 0)

  const onHand = deals.filter((d) => isOnHandStatus(d.deal_status))
  // §4.4: only a margin-scheme acquisition carries a notional credit, and it is
  // not claimable until the vehicle sells — so the tile is what is sitting on
  // stock, not what has been claimed.
  const div66 = onHand.reduce(
    (s, d) => s + (treatmentOf(d) === 'notional_itc' ? gstComponent(d.valuation_amount || 0) : 0),
    0
  )

  /*
   * Winnie, item 3: "Need to show the figure is actual MTD."
   *
   * The headline above is a balance, not a period figure - the notional credit
   * sitting in unsold stock as at today. It never was month-to-date, and the
   * card said nothing either way, which is what the note is about.
   *
   * A Division 66 credit is only released when the vehicle sells, so the actual
   * month-to-date number is the credit unlocked by this month's sales. Both now
   * show, each named, so nobody has to guess which basis they are reading.
   */
  const div66UnlockedMtd = soldThisMonth.reduce(
    (s, d) => s + (treatmentOf(d) === 'notional_itc' ? gstComponent(d.valuation_amount || 0) : 0),
    0,
  )

  // ---------- eyebrow + headline (a sentence that sums up the day) ----------

  const dateStr = now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })
  const eyebrow = firstName
    ? `${dateStr} · ${greetingFor(now.getHours())}, ${firstName}`
    : `${dateStr} · ${greetingFor(now.getHours())}`

  const headlineParts: string[] = []
  if (pendingRuns.length)
    headlineParts.push(`${pendingRuns.length} payment${pendingRuns.length === 1 ? '' : 's'} to approve`)
  const blockedDemands = overdueInvoices.filter((i) => daysSince(i.due_date) >= 30).length
  if (blockedDemands) headlineParts.push(`${blockedDemands} to sign off`)
  if (arOverdue > 0) headlineParts.push(`${kmoney(arOverdue)} to chase`)
  const headline = headlineParts.length
    ? headlineParts.join(', ')
    : onHand.length
      ? `You're all clear — ${onHand.length} vehicle${onHand.length === 1 ? '' : 's'} in stock`
      : "You're all clear"

  const sub =
    'Everything urgent is at the top. The system has already done the chasing, the checks and the maths — these are the only things that actually need you.'

  // ---------- hero stats ----------

  const hero: HeroStat[] = [
    {
      label: 'Net profit MTD',
      value: money(netProfitMtd),
      detail: soldThisMonth.length
        ? `${mtdRange} \u00b7 ${soldThisMonth.length} deal${soldThisMonth.length === 1 ? '' : 's'} closed`
        : `${mtdRange} \u00b7 no sales closed yet`,
      icon: 'ti-businessplan',
      trend: netProfitMtd > 0 ? 'up' : '',
      view: 'reports',
    },
    {
      label: 'Awaiting your approval',
      value: money(awaitingApproval),
      detail: pendingRuns.length
        ? `${pendingRuns.length} payment run${pendingRuns.length === 1 ? '' : 's'} queued`
        : 'Nothing waiting on you',
      icon: 'ti-cash',
      trend: pendingRuns.length ? 'down' : '',
      view: 'payment_run',
    },
    {
      label: 'AR overdue',
      value: money(arOverdue),
      detail: overdueInvoices.length
        ? `auto-chasing ${overdueInvoices.length} invoice${overdueInvoices.length === 1 ? '' : 's'}`
        : 'all invoices current',
      icon: 'ti-clock-dollar',
      trend: overdueInvoices.length ? 'down' : '',
      view: 'ar_reminders',
    },
    {
      label: 'Margin Scheme (GST Register) MTD',
      value: money(div66),
      detail: onHand.length
        ? `on hand as at today \u00b7 ${money(div66UnlockedMtd)} unlocked ${mtdRange}`
        : 'no stock on hand',
      icon: 'ti-receipt-tax',
      trend: '',
      view: 'gst_register',
    },
  ]

  // ---------- action stream ----------

  const needsYou: ActionItem[] = []

  if (winnieRuns.length) {
    const amt = winnieRuns.reduce((s, r) => s + runAmount(r), 0)
    needsYou.push({
      key: 'pay-winnie',
      kicker: 'Ready for first approval',
      title: `${winnieRuns.length} payment${winnieRuns.length === 1 ? '' : 's'} need your sign-off`,
      detail: 'Review the DD checks, then it routes to Tuo for the second approval before the WBC batch.',
      severity: 'urgent',
      icon: 'ti-cash',
      iconBg: 'var(--redbg)',
      iconColor: 'var(--red)',
      view: 'payment_run',
      amount: money(amt),
      amountColor: 'var(--red)',
    })
  }
  if (tuoRuns.length) {
    const amt = tuoRuns.reduce((s, r) => s + runAmount(r), 0)
    needsYou.push({
      key: 'pay-tuo',
      kicker: 'Ready to release',
      title: `${tuoRuns.length} payment${tuoRuns.length === 1 ? '' : 's'} cleared first approval`,
      detail: 'Second sign-off releases the funds to Westpac. PPSR re-validates automatically before release.',
      severity: 'go',
      icon: 'ti-circle-check',
      iconBg: 'var(--greenbg)',
      iconColor: 'var(--green)',
      view: 'payment_approval',
      amount: money(amt),
      amountColor: 'var(--green)',
    })
  }

  const ddPending = deals.filter(
    (d) => d.dd_submitted_at && !d.dd_approved_at && !['cancelled', 'closed'].includes(d.deal_status)
  )
  if (ddPending.length) {
    needsYou.push({
      key: 'dd-review',
      kicker: 'Due diligence',
      title: `${ddPending.length} DD pack${ddPending.length === 1 ? '' : 's'} to review`,
      detail:
        ddPending.slice(0, 2).map(vehicleLabel).join(', ') +
        (ddPending.length > 2 ? ` +${ddPending.length - 2} more` : ''),
      severity: 'urgent',
      icon: 'ti-checklist',
      iconBg: 'var(--amberbg)',
      iconColor: 'var(--amber)',
      view: 'dd_review',
    })
  }

  const formalDemand = overdueInvoices.filter((i) => daysSince(i.due_date) >= 30)
  if (formalDemand.length) {
    needsYou.push({
      key: 'ar-demand',
      kicker: 'Sign when ready',
      title: `${formalDemand.length} formal demand${formalDemand.length === 1 ? '' : 's'} to sign`,
      detail: `Auto-drafted at the 30-day mark. Your signature fires the recovery referral.`,
      severity: 'urgent',
      icon: 'ti-gavel',
      iconBg: 'var(--redbg)',
      iconColor: 'var(--red)',
      view: 'ar_reminders',
      amount: money(formalDemand.reduce((s, i) => s + invAmount(i), 0)),
      amountColor: 'var(--red)',
    })
  }

  const staleSignatures = deals.filter(
    (d) =>
      d.bill_of_sale_sent_at &&
      !d.elections_locked_at &&
      daysSince(d.bill_of_sale_sent_at) >= 3 &&
      !['cancelled', 'closed', 'sold'].includes(d.deal_status)
  )
  if (staleSignatures.length) {
    needsYou.push({
      key: 'bos-chase',
      kicker: 'Counter-sign / chase',
      title: `${staleSignatures.length} contract${staleSignatures.length === 1 ? '' : 's'} awaiting signature`,
      detail: `Sent ${Math.max(...staleSignatures.map((d) => daysSince(d.bill_of_sale_sent_at)))}+ days ago — may need a nudge.`,
      severity: 'info',
      icon: 'ti-writing',
      iconBg: 'var(--bluebg)',
      iconColor: 'var(--blue)',
      view: 'contracts',
    })
  }

  // ---------- coming up ----------

  const comingUp: ActionItem[] = []

  const inNegotiation = deals.filter((d) =>
    ['under_offer', 'contract_signed', 'committed'].includes(d.deal_status)
  )
  if (inNegotiation.length) {
    comingUp.push({
      key: 'in-negotiation',
      kicker: 'In the pipeline',
      title: `${inNegotiation.length} deal${inNegotiation.length === 1 ? '' : 's'} in progress`,
      detail: `${money(inNegotiation.reduce((s, d) => s + (d.valuation_amount || 0), 0))} of committed capital moving toward payment.`,
      severity: 'info',
      icon: 'ti-layout-kanban',
      iconBg: 'var(--purplebg)',
      iconColor: 'var(--purple)',
      view: 'pipeline',
    })
  }

  const readyToList = deals.filter((d) => d.deal_status === 'paid')
  if (readyToList.length) {
    comingUp.push({
      key: 'ready-list',
      kicker: 'Paid & in stock',
      title: `${readyToList.length} vehicle${readyToList.length === 1 ? '' : 's'} ready to list`,
      detail: 'List to start the resale clock and bring the margin scheme credit forward.',
      severity: 'go',
      icon: 'ti-building-warehouse',
      iconBg: 'var(--accdim)',
      iconColor: 'var(--acc)',
      view: 'pipeline',
    })
  }

  const dueSoon = openInvoices.filter((i) => {
    const d = daysSince(i.due_date)
    return d >= -3 && d <= 0
  })
  if (dueSoon.length) {
    comingUp.push({
      key: 'ar-due-soon',
      // Winnie's review: this label reads "Debtors (Accounts Receivable)".
      kicker: 'Debtors (Accounts Receivable)',
      title: `${dueSoon.length} invoice${dueSoon.length === 1 ? '' : 's'} due this week`,
      detail: `${money(dueSoon.reduce((s, i) => s + invAmount(i), 0))} expected — reminders already scheduled.`,
      severity: 'info',
      icon: 'ti-calendar-dollar',
      iconBg: 'var(--bluebg)',
      iconColor: 'var(--blue)',
      view: 'ar_reminders',
    })
  }

  // ---------- handled automatically ----------

  const handled: ActionItem[] = []

  const autoChasing = overdueInvoices.filter((i) => {
    const d = daysSince(i.due_date)
    return d >= 1 && d <= 13
  })
  if (autoChasing.length) {
    handled.push({
      key: 'ar-auto',
      kicker: 'Done for you',
      title: `${autoChasing.length} overdue invoice${autoChasing.length === 1 ? '' : 's'} chased automatically`,
      detail: 'Reminder cadence running on schedule per the collections rules.',
      severity: 'done',
      icon: 'ti-mail-fast',
      iconBg: 'var(--panel2)',
      iconColor: 'var(--txt2)',
      view: 'ar_reminders',
    })
  }

  const sourcedThisWeek = deals.filter(
    (d) => d.autograb_fetched_at && new Date(d.autograb_fetched_at).getTime() >= weekStart
  )
  if (sourcedThisWeek.length) {
    handled.push({
      key: 'sourced-week',
      kicker: 'Done for you',
      title: `${sourcedThisWeek.length} vehicle${sourcedThisWeek.length === 1 ? '' : 's'} valued & ranked`,
      detail: 'Sourced and priced via Autograb this week — ready in the radar.',
      severity: 'done',
      icon: 'ti-radar-2',
      iconBg: 'var(--panel2)',
      iconColor: 'var(--txt2)',
      view: 'sourced',
    })
  }

  const ppsrClear = deals.filter((d) => d.ppsr_status === 'clear').length
  if (ppsrClear) {
    handled.push({
      key: 'ppsr',
      kicker: 'Done for you',
      title: `${ppsrClear} PPSR check${ppsrClear === 1 ? '' : 's'} cleared`,
      detail: 'Encumbrance checks passed automatically before payment.',
      severity: 'done',
      icon: 'ti-shield-check',
      iconBg: 'var(--panel2)',
      iconColor: 'var(--txt2)',
      view: 'pipeline',
    })
  }

  // ---------- the brain — insights ----------

  const insights: BrainInsight[] = []

  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime()
  const soldLastMonth = deals.filter(
    (d) =>
      isSoldStatus(d.deal_status) &&
      new Date(d.updated_at).getTime() >= lastMonthStart &&
      new Date(d.updated_at).getTime() < monthStart
  )
  if (soldThisMonth.length || soldLastMonth.length) {
    const delta = soldThisMonth.length - soldLastMonth.length
    insights.push({
      kind: 'pace',
      label: 'Pace',
      value: delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : 'On pace',
      body: `${soldThisMonth.length} closed this month vs ${soldLastMonth.length} by this point last month.`,
      color: delta < 0 ? 'var(--amber)' : 'var(--green)',
      icon: delta < 0 ? 'ti-trending-down' : 'ti-trending-up',
      spark: ramp(soldThisMonth.length + 3, delta >= 0),
      view: 'reports',
    })
  }

  const agedStock = onHand.filter((d) => daysSince(d.paid_at || d.updated_at) >= 30)
  if (agedStock.length) {
    insights.push({
      kind: 'risk',
      label: 'Risk',
      value: `${agedStock.length} stale`,
      body: `${money(agedStock.reduce((s, d) => s + (d.valuation_amount || 0), 0))} held 30+ days. Prioritise listing to free up capital.`,
      color: 'var(--red)',
      icon: 'ti-alert-triangle',
      spark: ramp(agedStock.length + 5, true),
      view: 'pipeline',
    })
  } else if (arOverdue > 0) {
    insights.push({
      kind: 'risk',
      label: 'Risk',
      value: kmoney(arOverdue),
      body: `${overdueInvoices.length} invoice${overdueInvoices.length === 1 ? '' : 's'} overdue. Collections cadence is active.`,
      color: 'var(--red)',
      icon: 'ti-alert-triangle',
      spark: ramp(overdueInvoices.length + 4, true),
      view: 'ar_reminders',
    })
  }

  const strongMargin = deals.filter(
    (d) => (d.margin_percent || 0) >= 15 && ['under_offer', 'committed'].includes(d.deal_status)
  )
  if (strongMargin.length) {
    insights.push({
      kind: 'opportunity',
      label: 'Opportunity',
      value: `${strongMargin.length} live`,
      body: `High-margin deals averaging ${Math.round(strongMargin.reduce((s, d) => s + (d.margin_percent || 0), 0) / strongMargin.length)}% — worth pushing to close.`,
      color: 'var(--amber)',
      icon: 'ti-flame',
      spark: ramp(strongMargin.length + 2, true),
      view: 'pipeline',
    })
  } else if (div66 > 0) {
    insights.push({
      kind: 'opportunity',
      label: 'Opportunity',
      value: kmoney(div66),
      body: 'Margin scheme credits unlock as on-hand stock resells. Listing sooner brings the credit forward.',
      color: 'var(--amber)',
      icon: 'ti-clock-dollar',
      spark: ramp(onHand.length + 6, false),
      view: 'reports',
    })
  }

  // ---------- recommendation ----------

  let recommendation: DashboardSnapshot['recommendation'] = null
  if (needsYou.length) {
    const top = needsYou[0]
    recommendation = {
      text: `Suggested focus: clear ${needsYou.length} item${needsYou.length === 1 ? '' : 's'} in "Needs you now" — starting with ${top.title.toLowerCase()}.`,
      view: top.view,
    }
  } else if (comingUp.length) {
    recommendation = {
      text: `Nothing urgent. When you have a moment, ${comingUp[0].title.toLowerCase()}.`,
      view: comingUp[0].view,
    }
  }

  // ---------- deal flow ----------

  const stageCount = (statuses: Deal['deal_status'][]) =>
    deals.filter((d) => statuses.includes(d.deal_status)).length

  /*
   * Labels come from the status registry, not from this file.
   *
   * These were seven hardcoded strings, and one had already drifted: this funnel said
   * 'Offer' where the registry says 'Offer Accepted' - the exact rename the registry was
   * given so that "the pipeline column, the card, the chip and the button all agree". The
   * handover README's cross-cutting rule is that no screen invents its own label for a
   * vehicle status. Found by the 14 September spec audit.
   *
   * The one string still written here is 'Sold', because that stage groups five statuses
   * and no single registry row names the group. A grouping is not a status display - the
   * same carve-out the Stock Management filter chips have.
   */
  const stage = (nameFrom: DealStatus, statuses: DealStatus[]): FlowStage => ({
    label: statusLabel(nameFrom),
    count: stageCount(statuses),
  })

  const flow: FlowStage[] = [
    stage('sourced', ['sourced']),
    stage('under_offer', ['under_offer']),
    stage('committed', ['contract_signed', 'committed']),
    stage('approved_for_payment', ['approved_for_payment']),
    stage('paid', ['paid', 'listed']),
    stage('deal_pending', ['deal_pending']),
    {
      label: 'Sold',
      count: stageCount(['sold', 'sold_in_transit', 'sold_delivered', 'awaiting_payment', 'closed']),
    },
  ]
  return {
    eyebrow,
    headline,
    sub,
    hero,
    needsYou,
    comingUp,
    handled,
    insights,
    recommendation,
    flow,
  }
}
